import { errorToast } from "@/shared/lib/apiErrors";
import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Layers, Loader2, Plus, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { useToast } from "@/shared/hooks/use-toast";
import { useBrandAssets } from "@/entities/brand-asset";
import { cn } from "@/shared/lib/utils";

import { posterService } from "../../../api/posterService";
import type { OverlayConfig as OverlayConfigV6 } from "../../../api/posterTemplateService";

/** 0713 批量品牌变体上限(对齐后端 MAX_VARIANTS_PER_BATCH / 打包下载上限)。 */
const MAX_ROWS = 10;

interface RowDraft {
  key: number;
  logoId: string; // brand_assets.logos[].id;"" = 不加
  qrId: string; // brand_assets.qrs[].id;"" = 不加
  name: string;
  /** 用户手改过命名后,切换 logo 不再自动覆盖 */
  nameTouched: boolean;
}

/**
 * 批量生成品牌变体弹窗(0713「一行一张图」):
 * - 一行 = 一张成品图,每行独立点选 logo + 二维码(任一可空,不能全空);
 * - 位置锚点沿用编辑器当前摆放(v6Overlay 的 logo/qr bbox),全行共用;
 * - 快捷键「全部 logo 各出一张」/「同名自动配对」;相同组合去重、上限 10 行;
 * - 生成走 POST /variants(确定性叠合产新版本,不扣费),部分失败 toast 报行名+原因。
 */
export function BrandVariantsDialog({
  open,
  onOpenChange,
  taskId,
  assetId,
  v6Overlay,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: number;
  assetId: number;
  v6Overlay: OverlayConfigV6;
  onDone: () => Promise<void> | void;
}) {
  const { t } = useTranslation("poster");
  const { toast } = useToast();
  const { data: brandAssets, isLoading } = useBrandAssets();
  const logos = useMemo(() => brandAssets?.logos ?? [], [brandAssets]);
  const qrs = useMemo(() => brandAssets?.qrs ?? [], [brandAssets]);

  const [rows, setRows] = useState<RowDraft[]>([]);
  const [nextKey, setNextKey] = useState(1);

  const takeKeys = (n: number) => {
    setNextKey((k) => k + n);
    return Array.from({ length: n }, (_, i) => nextKey + i);
  };

  const autoName = (logoId: string, qrId: string) => {
    const logo = logos.find((l) => l.id === logoId);
    if (logo) return `${t("variants.namePrefix")}${logo.name || logo.id}`;
    const qr = qrs.find((q) => q.id === qrId);
    if (qr) return `${t("variants.namePrefix")}${qr.name || qr.id}`;
    return "";
  };

  const addRow = () => {
    if (rows.length >= MAX_ROWS) return;
    const [key] = takeKeys(1);
    setRows((r) => [...r, { key, logoId: "", qrId: "", name: "", nameTouched: false }]);
  };

  /** 快捷:素材库每个 logo 一行(二维码不加,用户可逐行补),截断到上限。 */
  const fillAllLogos = () => {
    const take = logos.slice(0, MAX_ROWS);
    const keys = takeKeys(take.length);
    setRows(
      take.map((logo, i) => ({
        key: keys[i],
        logoId: logo.id,
        qrId: "",
        name: `${t("variants.namePrefix")}${logo.name || logo.id}`,
        nameTouched: false,
      }))
    );
    if (logos.length > MAX_ROWS) {
      toast({ title: t("variants.truncatedToCap", { cap: MAX_ROWS }) });
    }
  };

  /** 快捷:logo 与二维码按同名配对(渠道场景素材命名规范:经销商A logo + 经销商A 码)。 */
  const fillPairedByName = () => {
    const paired = logos
      .map((logo) => ({ logo, qr: qrs.find((q) => q.name && q.name === logo.name) }))
      .filter((p) => p.qr)
      .slice(0, MAX_ROWS);
    if (paired.length === 0) {
      toast({
        title: t("variants.noNameMatch"),
        description: t("variants.noNameMatchDesc"),
      });
      return;
    }
    const keys = takeKeys(paired.length);
    setRows(
      paired.map((p, i) => ({
        key: keys[i],
        logoId: p.logo.id,
        qrId: p.qr!.id,
        name: `${t("variants.namePrefix")}${p.logo.name}`,
        nameTouched: false,
      }))
    );
  };

  const patchRow = (key: number, patch: Partial<RowDraft>) => {
    setRows((rs) =>
      rs.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r, ...patch };
        if ((patch.logoId !== undefined || patch.qrId !== undefined) && !next.nameTouched) {
          next.name = autoName(next.logoId, next.qrId);
        }
        return next;
      })
    );
  };

  // 相同 (logo, qr) 组合视为重复(后端也会去重,前端提前标出来)
  const duplicateKeys = useMemo(() => {
    const seen = new Map<string, number>();
    const dup = new Set<number>();
    for (const r of rows) {
      const combo = `${r.logoId}|${r.qrId}`;
      if (r.logoId === "" && r.qrId === "") continue;
      if (seen.has(combo)) dup.add(r.key);
      else seen.set(combo, r.key);
    }
    return dup;
  }, [rows]);

  const validRows = rows.filter(
    (r) => (r.logoId !== "" || r.qrId !== "") && !duplicateKeys.has(r.key)
  );

  const genMut = useMutation({
    mutationFn: async () => {
      const overlayConfig: Record<string, unknown> = {};
      if (v6Overlay.logo) overlayConfig.logo = v6Overlay.logo;
      if (v6Overlay.qr) overlayConfig.qrcode = v6Overlay.qr;
      return posterService.generateBrandVariants(taskId, assetId, {
        overlayConfig: Object.keys(overlayConfig).length > 0 ? overlayConfig : null,
        variants: validRows.map((r) => ({
          name: r.name.trim() || undefined,
          logoRef: r.logoId || undefined,
          qrRef: r.qrId || undefined,
        })),
      });
    },
    onSuccess: async (resp) => {
      if (resp.failed.length > 0) {
        toast({
          title: t("variants.partialDone", {
            ok: resp.created.length,
            fail: resp.failed.length,
          }),
          description: resp.failed
            .map((f) => `${f.name ?? t("variants.unnamedRow")}: ${f.message}`)
            .join("；"),
          variant: "destructive",
        });
      } else {
        toast({ title: t("variants.allDone", { ok: resp.created.length }) });
      }
      onOpenChange(false);
      setRows([]);
      await onDone();
    },
    onError: (e: unknown) => {
      toast(errorToast(e, { fallbackTitle: t("variants.failed") }));
    },
  });

  const selectClass =
    "h-7 w-full min-w-0 rounded border border-border/60 bg-background px-1.5 text-xs";

  return (
    <Dialog open={open} onOpenChange={(o) => !genMut.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            {t("variants.title")}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground">{t("variants.positionHint")}</p>

        {isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("variants.loadingBrand")}
          </div>
        ) : logos.length === 0 && qrs.length === 0 ? (
          <div className="rounded border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground">
            {t("variants.noBrandAssets")}
          </div>
        ) : (
          <>
            <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
              {rows.length > 0 && (
                <div className="grid grid-cols-[1fr_1fr_1fr_24px] gap-1.5 px-0.5 text-2xs text-muted-foreground">
                  <span>Logo</span>
                  <span>{t("freeCreate.qrcode")}</span>
                  <span>{t("variants.colName")}</span>
                  <span />
                </div>
              )}
              {rows.map((r) => {
                const emptyRow = r.logoId === "" && r.qrId === "";
                const isDup = duplicateKeys.has(r.key);
                return (
                  <div
                    key={r.key}
                    className={cn(
                      "grid grid-cols-[1fr_1fr_1fr_24px] items-center gap-1.5 rounded border border-border/40 p-1.5",
                      (emptyRow || isDup) && "border-warning/60 bg-warning/5"
                    )}
                  >
                    <select
                      aria-label="Logo"
                      className={selectClass}
                      value={r.logoId}
                      onChange={(e) => patchRow(r.key, { logoId: e.target.value })}
                    >
                      <option value="">{t("variants.noneOption")}</option>
                      {logos.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name || l.id}
                        </option>
                      ))}
                    </select>
                    <select
                      aria-label={t("freeCreate.qrcode")}
                      className={selectClass}
                      value={r.qrId}
                      onChange={(e) => patchRow(r.key, { qrId: e.target.value })}
                    >
                      <option value="">{t("variants.noneOption")}</option>
                      {qrs.map((q) => (
                        <option key={q.id} value={q.id}>
                          {q.name || q.id}
                        </option>
                      ))}
                    </select>
                    <input
                      aria-label={t("variants.colName")}
                      className={selectClass}
                      value={r.name}
                      placeholder={t("variants.namePlaceholder")}
                      onChange={(e) => patchRow(r.key, { name: e.target.value, nameTouched: true })}
                    />
                    <button
                      type="button"
                      aria-label={t("variants.removeRow")}
                      onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
                      className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-destructive"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
              {rows.some((r) => r.logoId === "" && r.qrId === "") && (
                <p className="px-0.5 text-2xs text-warning">{t("variants.emptyRowHint")}</p>
              )}
              {duplicateKeys.size > 0 && (
                <p className="px-0.5 text-2xs text-warning">{t("variants.dupRowHint")}</p>
              )}
            </div>

            <div className="flex flex-wrap gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                disabled={rows.length >= MAX_ROWS}
                onClick={addRow}
              >
                <Plus className="h-3 w-3" />
                {t("variants.addRow")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={logos.length === 0}
                onClick={fillAllLogos}
              >
                {t("variants.fillAllLogos")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={logos.length === 0 || qrs.length === 0}
                onClick={fillPairedByName}
              >
                {t("variants.pairByName")}
              </Button>
            </div>
          </>
        )}

        <DialogFooter className="flex items-center gap-3 sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {t("variants.willGenerate", { count: validRows.length, cap: MAX_ROWS })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={genMut.isPending}
              onClick={() => onOpenChange(false)}
            >
              {t("variants.cancel")}
            </Button>
            <Button
              size="sm"
              className="gap-1"
              disabled={validRows.length === 0 || genMut.isPending}
              onClick={() => genMut.mutate()}
            >
              {genMut.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Layers className="h-3 w-3" />
              )}
              {t("variants.generate")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
